// src/components/TimeTracking.js
// New Time Tracking UI following PRD_TimeTrackingUI specifications
// Implements daily view with 3-month navigation limit and employee filtering by dates

import React, { useState, useEffect, useMemo, useRef } from "react";
import Icon from "./icons/Icon";
import { DayFlagBadge } from "./DayFlagBadge";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import AddTimeRecordModal from "./modals/AddTimeRecordModal";
import { filterEmployeesByDate } from "../utils/employeeFilters";
import { matchesEmployeeSearch } from "../utils/employeeSearch";
import { formatDateString } from "../utils/timeCalculations";
// hasLateRecord is gone from this file: C4's roster carve-out kept the sort late-only
// because there was no flags column to mislabel, and C10 then added one — so the sort
// moved onto the FLAGS header and widened to hasAnyFlaggedRecord, matching the monthly
// table. The precise late-only tools are still the `late` search token and, on the
// monthly page, the Late Only dropdown option.
import { isSearchToken, hasFlaggedRecord, hasAnyFlaggedRecord, hasWorkedRecord, dayFlagOf, emptyReasonFor } from "../utils/dayFlags";
import { formatFilteredCount } from "../utils/footerStats";
import {
  formatFirestoreTimestamp,
  getMonthKey,
  getAdjacentMonth,
  isMonthBoundary,
  filterRecordsByDate
} from "../utils/timeFormatting";
import "../styles/sections/time-tracking.css";

/**
 * Format date for display label
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (MMM DD, YYYY)
 */
const formatDateLabel = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Calculate navigation state for date buttons
 * @param {Date} selectedDate - Currently selected date
 * @returns {Object} - States for previous and next buttons
 */
const getNavigationState = (selectedDate) => {
  // Use local timezone
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
  const selected = new Date(selectedDate);
  selected.setHours(0, 0, 0, 0);

  return {
    isPreviousDisabled: selected <= threeMonthsAgo,
    isNextDisabled: selected >= today
  };
};

/**
 * Fetch one month's DTRs for the whole company in a single query (C1/C2).
 * Mirrors OTApprovalPage.fetchData.
 *
 * Rejects on a cache-only snapshot: getDocs RESOLVES from the local cache when the
 * backend is unreachable rather than rejecting, and with this app's memory-only cache
 * that is an empty result — indistinguishable from a genuinely empty month, so the page
 * would read "nobody worked" during an outage. `fromCache` is a precise offline signal
 * here because getDocs waits for the server sync while it believes it is online.
 *
 * @param {string} companyDocId - company document id
 * @param {string} monthKey - 'YYYY-MM'
 * @returns {Promise<Object>} { employeeId: records[] }
 */
const fetchMonthRecords = async (companyDocId, monthKey) => {
  const snap = await getDocs(query(
    collection(db, 'companies', companyDocId, 'dailyTimeRecords'),
    where('month', '==', monthKey)
  ));

  if (snap.metadata.fromCache) {
    throw new Error(`Time records for ${monthKey} are unavailable (offline)`);
  }

  const byEmployee = {};
  snap.forEach(d => {
    const data = d.data();
    if (data.employeeId) byEmployee[data.employeeId] = data.timeRecords || [];
  });
  return byEmployee;
};

export const TimeTracking = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user, company, hasPermission } = useAuth();
  const canEditTime = hasPermission('time:edit');

  // State management
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [companyDocId, setCompanyDocId] = useState(null);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showAddModal, setShowAddModal] = useState(false);

  // New state for UI redesign
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // DTR caching
  const [dtrCache, setDtrCache] = useState({}); // { 'yyyy-mm': { employeeId: records[] } }
  const [failedMonths, setFailedMonths] = useState(() => new Set()); // months whose query rejected (C7)
  // Months already requested this session. A ref, not state, on purpose: it exists only
  // to stop a month being queried twice while in flight, and putting it in state would
  // re-introduce the render dependency that caused BL-081.
  const requestedMonthsRef = useRef(new Set());
  const prevCompanyRef = useRef(null);

  // Payroll rates (financial rates only — kept for other features)
  const [payrollRates, setPayrollRates] = useState(null);
  const [payrollRatesError, setPayrollRatesError] = useState(null);

  // Attendance policy for ND window, defaults, and leeway
  const [attendancePolicy, setAttendancePolicy] = useState(null);

  // Navigation button states
  const navigationState = getNavigationState(selectedDate);

  // Handle responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Company document ID from the auth context — the signed-in user's own company.
  // A slug query across `companies` is denied (PRD_CompanyAccessRules C2).
  useEffect(() => {
    if (company?.id) setCompanyDocId(company.id);
  }, [company]);

  // Fetch payroll rates (financial rates only) and attendance policy (ND, defaults, leeway)
  useEffect(() => {
    const fetchSettings = async () => {
      if (!companyDocId) return;

      try {
        // Fetch payroll rates (financial rates)
        const ratesDoc = await getDoc(doc(db, 'companies', companyDocId, 'settings', 'payrollRates'));
        if (ratesDoc.exists()) {
          setPayrollRates(ratesDoc.data());
        }

        // Fetch attendance policy (ND window, defaults, leeway)
        const policyDoc = await getDoc(doc(db, 'companies', companyDocId, 'settings', 'attendancePolicy'));
        if (policyDoc.exists()) {
          const policyData = policyDoc.data();
          if (typeof policyData.nightDiffStart === 'number' && typeof policyData.nightDiffEnd === 'number') {
            setAttendancePolicy(policyData);
            setPayrollRatesError(null);
          } else {
            setPayrollRatesError('Night Differential settings are missing. Please configure them in Settings > Attendance Policy.');
          }
        } else {
          setPayrollRatesError('Attendance policy not configured. Please set it up in Settings > Attendance Policy.');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setPayrollRatesError('Failed to load settings.');
      }
    };

    fetchSettings();
  }, [companyDocId]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!companyDocId) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch employees from employees subcollection
        const employeesRef = collection(db, "companies", companyDocId, "employees");

        // Query employees directly - no index needed since we'll sort client-side
        const employeesQuery = query(
          employeesRef,
          where("role", "==", "employee")
        );
        const employeesSnapshot = await getDocs(employeesQuery);

        const employeesList = [];

        employeesSnapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          employeesList.push(data);
        });

        // Sort in memory if we used the fallback query
        employeesList.sort((a, b) => {
          const idA = a.employeeId || '';
          const idB = b.employeeId || '';
          return idA.localeCompare(idB);
        });

        setEmployees(employeesList);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Failed to load employee data");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [companyDocId]);

  // Filter and sort employees when date or sort changes
  useEffect(() => {
    let filtered = filterEmployeesByDate(employees, selectedDate);

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case "name":
            // Sort by lastName first, then firstName as tiebreaker
            aValue = (a.personalInfo?.lastName || '').toLowerCase();
            bValue = (b.personalInfo?.lastName || '').toLowerCase();
            if (aValue === bValue) {
              aValue = (a.personalInfo?.firstName || '').toLowerCase();
              bValue = (b.personalInfo?.firstName || '').toLowerCase();
            }
            break;
          case "id":
            aValue = a.employeeId || '';
            bValue = b.employeeId || '';
            break;
          case "department":
            aValue = (a.employment?.department || '').toLowerCase();
            bValue = (b.employment?.department || '').toLowerCase();
            break;
          default:
            aValue = '';
            bValue = '';
        }

        // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
        // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
        // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
        const c = typeof aValue === "string" && typeof bValue === "string"
          ? aValue.localeCompare(bValue)
          : aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortDirection === "asc" ? c : -c;
      });
    }

    setFilteredEmployees(filtered);
  }, [employees, selectedDate, sortField, sortDirection]);

  // Fetch the viewed month's DTRs (and the adjacent month at a boundary) once each.
  //
  // Depends on [companyDocId, selectedDate] and NOT on filteredEmployees: the query is
  // per-month, not per-employee, which is what removes the double-invocation that caused
  // BL-081. There is deliberately no cleanup and no `cancelled` flag — both writes below
  // are keyed by monthKey, so a late result is still correct for the month it names (C11).
  useEffect(() => {
    if (!companyDocId) return;

    // C9 — tenant switch: drop the previous company's cache BEFORE any query is issued.
    if (prevCompanyRef.current && prevCompanyRef.current !== companyDocId) {
      requestedMonthsRef.current = new Set();
      setDtrCache({});
      setFailedMonths(new Set());
    }
    prevCompanyRef.current = companyDocId;

    // The tenant this run belongs to. This guard alone is sufficient — do NOT also
    // capture requestedMonthsRef.current (C9).
    const forCompany = companyDocId;

    const viewedMonth = getMonthKey(selectedDate);
    const months = [viewedMonth];
    if (isMonthBoundary(selectedDate)) {
      months.push(getAdjacentMonth(selectedDate,
        selectedDate.getDate() === 1 ? 'previous' : 'next'));
    }
    const missing = months.filter(m => !requestedMonthsRef.current.has(m));
    if (missing.length === 0) return;
    missing.forEach(m => requestedMonthsRef.current.add(m));

    // allSettled, not all: one month failing must not discard the other's good data.
    Promise.allSettled(missing.map(m => fetchMonthRecords(forCompany, m)))
      .then(results => {
        // C9 — a result from the previous tenant must never reach the new tenant's cache.
        if (forCompany !== prevCompanyRef.current) return;

        const fulfilled = {};
        const failed = [];
        results.forEach((r, i) => {
          const m = missing[i];
          if (r.status === 'fulfilled') {
            fulfilled[m] = r.value;
          } else {
            requestedMonthsRef.current.delete(m); // release for retry (C7)
            failed.push(m);
            console.error(`Error fetching time records for ${m}:`, r.reason);
          }
        });

        if (Object.keys(fulfilled).length > 0) {
          setDtrCache(prev => ({ ...prev, ...fulfilled }));
        }
        if (failed.length > 0 || Object.keys(fulfilled).length > 0) {
          setFailedMonths(prev => {
            const next = new Set(prev);
            Object.keys(fulfilled).forEach(m => next.delete(m));
            failed.forEach(m => next.add(m));
            return next;
          });
        }
      });
  }, [companyDocId, selectedDate]);

  // C7 — the viewed month's query rejected. Derived at render so it self-clears: a
  // successful retry deletes the month from failedMonths, and navigating to any other
  // month is simply a different key.
  const recordsFailed = failedMonths.has(getMonthKey(selectedDate));
  const failedMonthLabel = selectedDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  // Compute employees with their time records
  const employeesWithRecords = useMemo(() => {
    const currentMonth = getMonthKey(selectedDate);
    const monthCache = dtrCache[currentMonth] || {};
    const dateString = formatDateString(selectedDate);

    // Check if we need to include adjacent month records for overnight shifts
    let adjacentMonthCache = {};
    if (isMonthBoundary(selectedDate)) {
      const day = selectedDate.getDate();
      const adjacentMonth = getAdjacentMonth(selectedDate, day === 1 ? 'previous' : 'next');
      adjacentMonthCache = dtrCache[adjacentMonth] || {};
    }

    const withRecords = filteredEmployees.map(employee => {
      // Combine records from current and adjacent month if at boundary
      const currentMonthRecords = monthCache[employee.employeeId] || [];
      const adjacentMonthRecords = adjacentMonthCache[employee.employeeId] || [];
      const allRecords = [...currentMonthRecords, ...adjacentMonthRecords];

      const todayRecords = filterRecordsByDate(allRecords, dateString);

      return {
        ...employee,
        timeEntries: todayRecords  // Already sorted by filterRecordsByDate
      };
    });

    // FLAGS sort runs here (not in the name/id/dept sort effect) because the day-type
    // flags live on the per-day timeEntries attached above. ANY flagged employee first —
    // late, piece rate, either leave, or absent — tiebroken by surname asc.
    //
    // Was late-only, bound to the Time In header (C4's original roster carve-out). That
    // carve-out rested on "the roster has no flags column, so there is no mislabelled
    // affordance to correct" — which stopped being true when C10 gained one. Sorting by
    // lateness from a header labelled Time In, while the value sorted on rendered two
    // columns away, was the affordance C4 exists to prevent. Now matches the monthly
    // table exactly (EmployeeTimeRecords.js:345-352).
    if (sortField === "late") {
      return [...withRecords].sort((a, b) => {
        const aLate = hasAnyFlaggedRecord(a.timeEntries);
        const bLate = hasAnyFlaggedRecord(b.timeEntries);
        if (aLate !== bLate) {
          return sortDirection === "asc" ? (aLate ? -1 : 1) : (aLate ? 1 : -1);
        }
        const aName = (a.personalInfo?.lastName || '').toLowerCase();
        const bName = (b.personalInfo?.lastName || '').toLowerCase();
        // localeCompare, matching the Employee column above — a raw < / > would
        // sort Ñuñez last here while the same table's Employee column sorts it
        // under N (C11).
        return aName.localeCompare(bName);
      });
    }

    return withRecords;
  }, [filteredEmployees, dtrCache, selectedDate, sortField, sortDirection]);

  // Calculate time tracking stats for stats cards
  const timeTrackingStats = useMemo(() => {
    const stats = {
      total: employeesWithRecords.length,
      clockedIn: 0,
      notClockedIn: 0,
      departments: new Set()
    };

    employeesWithRecords.forEach(employee => {
      if (employee.timeEntries && employee.timeEntries.length > 0) {
        stats.clockedIn++;
      } else {
        stats.notClockedIn++;
      }

      if (employee.employment?.department) {
        stats.departments.add(employee.employment.department);
      }
    });

    return {
      ...stats,
      departmentsList: Array.from(stats.departments).sort(),
      departmentsCount: stats.departments.size
    };
  }, [employeesWithRecords]);

  // Filter employees by search and department
  const displayedEmployees = useMemo(() => {
    let result = [...employeesWithRecords];

    // Apply search filter. Typing a day-type token — or any PREFIX of one —
    // switches the box from text matching to flag filtering (BL-006) for the
    // SELECTED DAY, since this page is a single-day view. The search box is the
    // ONLY route here: this page already carries a Department dropdown, and a
    // second dropdown made the controls bar too crowded (founder call at
    // /test-impl). The Employee Time Records page keeps its dropdown, where the
    // option slots into an existing filter rather than adding a new control.
    //
    // Because it is a prefix test, a name whose leading letters prefix a token
    // filters by that flag for those keystrokes — `Abad` filters by `absent`
    // for `a` and `ab`, then falls back to name matching at `aba`. Deliberate;
    // see the trade-off documented on matchingTokens in utils/dayFlags.js.
    if (searchTerm) {
      if (isSearchToken(searchTerm)) {
        // BL-083: when the viewed month didn't load there are no timeEntries for
        // anyone, so ANY flag predicate would filter out every row and the page
        // would claim "No Matching Employees". Suspend the whole flag-filter
        // branch — not just the late one — and let the '?' rows show under the
        // failure banner instead.
        //
        // KNOWN, deliberately unhandled: prefix matching widened the set of
        // terms that land here, so on a failed fetch a 1-2 character NAME search
        // is suspended too and the roster shows unfiltered for those keystrokes
        // rather than name-matching. Reaching it needs the fetch to fail AND a
        // short term; it costs one keystroke of an unfiltered list on a page
        // already showing a failure banner, and it self-corrects on the next
        // character. Not worth a second predicate to separate the two cases.
        if (!recordsFailed) {
          result = result.filter(emp => hasFlaggedRecord(emp.timeEntries, searchTerm));
        }
      } else {
        // The shared rule plus email, this page's extra term (C3).
        result = result.filter(emp =>
          matchesEmployeeSearch(emp, searchTerm, [emp.personalInfo?.email])
        );
      }
    }

    // Apply department filter
    if (filterDepartment) {
      result = result.filter(emp => emp.employment?.department === filterDepartment);
    }

    return result;
  }, [employeesWithRecords, searchTerm, filterDepartment, recordsFailed]);

  // BL-083(2): the footer sits under the FILTERED table, but its Clocked In /
  // Not Clocked In figures came from the pre-filter stats while Total was
  // post-filter — a one-department view read "Total 7 / Not Clocked In 26".
  // Count the same list the table renders.
  // C11: "clocked in" means a record that is neither leave nor absent — an
  // employee explicitly marked Absent was previously counted as clocked in,
  // because any record at all satisfied the old `length > 0` test. Leave and
  // absent employees therefore fall into "Not Clocked In", which is derived by
  // subtraction. hasWorkedRecord is shared with the monthly Days Worked (C5) so
  // the two definitions cannot drift.
  const displayedClockedIn = displayedEmployees.filter(
    (emp) => hasWorkedRecord(emp.timeEntries)
  ).length;

  // Filter-aware footer stats (PRD_FilterAwareFooterStats C1/C3): same untrimmed
  // condition the filter memo above tests. The dual Total and the Departments
  // count cover the FILTERED list; timeTrackingStats stays page-wide because it
  // feeds the department dropdown options.
  const filterActive = Boolean(searchTerm) || Boolean(filterDepartment);
  const displayedTotal = formatFilteredCount(
    displayedEmployees.length,
    employeesWithRecords.length,
    filterActive
  );
  const displayedDepartmentsCount = new Set(
    displayedEmployees
      .map((emp) => emp.employment?.department)
      .filter(Boolean)
  ).size;

  // Why the list is empty. The empty state is gated on the POST-filter list, so
  // it used to claim "no employees were active on <date>" even when plenty were
  // and the search simply matched nothing — which reads as "nobody worked that
  // day". Distinguish the three real cases (surfaced at /test-impl, BL-006).
  // The selector is a pure helper so it can be tested — the department case it
  // now guards was a live bug that no component test could have caught here.
  const emptyReason = emptyReasonFor({
    activeCount: filteredEmployees.length,
    searchTerm,
    filterDepartment,
  });

  // Date navigation handlers
  const handlePreviousDay = () => {
    if (navigationState.isPreviousDisabled) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    if (navigationState.isNextDisabled) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort Icon Component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  // Handle Add Time Record modal
  const handleAddTimeRecord = () => {
    setShowAddModal(true);
  };

  // Handle successful save from modal - refresh the employees list
  const handleSaveSuccess = async (savedRecord) => {
    // Optimistically update the cache with the new record. With the realtime listener
    // gone (C4) this patch is the only immediate-consistency mechanism; the month is
    // re-read from the server on the next fetch of that month.
    if (savedRecord) {
      // The record's own Manila date names its month doc (C3) — not the browser's view
      // of the time-in instant.
      const monthKey = savedRecord.date.slice(0, 7);
      const employeeId = savedRecord.employeeId;

      setDtrCache(prevCache => {
        const monthCache = prevCache[monthKey] || {};
        const employeeRecords = monthCache[employeeId] || [];

        return {
          ...prevCache,
          [monthKey]: {
            ...monthCache,
            [employeeId]: [...employeeRecords, savedRecord]
          }
        };
      });
    }

    setShowAddModal(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="time-tracking-container">
        <div className="time-loading">
          <div className="time-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="time-tracking-container">
        <div className="time-empty-state">
          <div className="time-empty-icon">
            <Icon name="alert-triangle" style={{ width: "48px", height: "48px", stroke: "var(--ui-danger)" }} />
          </div>
          <h3 className="time-empty-title">Error Loading Data</h3>
          <p className="time-empty-text">{error}</p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        {/* Page Title */}
        <h1
          style={{
            fontSize: isMobile ? "1.125rem" : "1.25rem",
            fontWeight: "700",
            color: "var(--ui-primary)",
            margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
          }}
        >
          Time Tracking
        </h1>

        {/* Stats Cards - Mobile only (desktop stats moved to table footer) */}
        {isMobile && <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          {/* Total Employees Card */}
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "15%",
                bottom: "15%",
                width: "4px",
                background: "var(--ui-primary)",
                borderRadius: "0 4px 4px 0",
              }}
            />
            <div
              style={{
                fontSize: "0.6875rem",
                color: "var(--ui-text-muted)",
                textTransform: "uppercase",
                fontWeight: "600",
                letterSpacing: "0.025em",
                marginBottom: "0.25rem",
              }}
            >
              Total Employees
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {displayedTotal}
            </div>
          </div>

          {/* Clocked In Card */}
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "15%",
                bottom: "15%",
                width: "4px",
                background: "var(--ui-success)",
                borderRadius: "0 4px 4px 0",
              }}
            />
            <div
              style={{
                fontSize: "0.6875rem",
                color: "var(--ui-text-muted)",
                textTransform: "uppercase",
                fontWeight: "600",
                letterSpacing: "0.025em",
                marginBottom: "0.25rem",
              }}
            >
              Clocked In
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {recordsFailed ? "—" : displayedClockedIn}
            </div>
          </div>

          {/* Not Clocked In Card */}
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "1.25rem 1.25rem 1.25rem 1.5rem",
              border: "1px solid var(--ui-border)",
              borderLeft: "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "15%",
                bottom: "15%",
                width: "4px",
                background: "var(--ui-danger)",
                borderRadius: "0 4px 4px 0",
              }}
            />
            <div
              style={{
                fontSize: "0.6875rem",
                color: "var(--ui-text-muted)",
                textTransform: "uppercase",
                fontWeight: "600",
                letterSpacing: "0.025em",
                marginBottom: "0.25rem",
              }}
            >
              Not Clocked In
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>
              {recordsFailed ? "—" : displayedEmployees.length - displayedClockedIn}
            </div>
          </div>

          {/* Departments Card */}
          <div
            style={{
              background: "var(--ui-primary)",
              borderRadius: "8px",
              padding: "1.25rem",
            }}
          >
            <div
              style={{
                fontSize: "0.6875rem",
                color: "rgba(255,255,255,0.8)",
                textTransform: "uppercase",
                fontWeight: "600",
                letterSpacing: "0.025em",
                marginBottom: "0.25rem",
              }}
            >
              Departments
            </div>
            <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "white" }}>
              {displayedDepartmentsCount}
            </div>
          </div>
        </div>}

        {/* Controls Section */}
        {isMobile ? (
          /* Mobile: stacked controls */
          <div
            style={{
              background: "white",
              padding: "1rem",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "1rem",
            }}
          >
            {/* Date Navigation Row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.75rem",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={handlePreviousDay}
                disabled={navigationState.isPreviousDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: navigationState.isPreviousDisabled ? "not-allowed" : "pointer",
                  opacity: navigationState.isPreviousDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                }}
              >
                &lt;
              </button>
              <button
                onClick={handleToday}
                style={{
                  padding: "0.375rem 0.75rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.8125rem",
                }}
              >
                Today
              </button>
              <button
                onClick={handleNextDay}
                disabled={navigationState.isNextDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: navigationState.isNextDisabled ? "not-allowed" : "pointer",
                  opacity: navigationState.isNextDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                }}
              >
                &gt;
              </button>
              <span
                style={{
                  marginLeft: "0.25rem",
                  fontWeight: "500",
                  color: "var(--ui-text-secondary)",
                  fontSize: "0.875rem",
                }}
              >
                {formatDateLabel(selectedDate)}
              </span>
            </div>

            {/* Search — typing a day-type token filters to it (BL-006, C8):
                `late`, `piece rate`, `paid leave`, `unpaid leave`, `absent`,
                or `leave` for either kind. */}
            <input
              type="text"
              data-testid="tt-search"
              placeholder="Search employees or 'late', 'absent'..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                marginBottom: "0.5rem",
                fontFamily: "inherit",
              }}
            />

            {/* Department Filter */}
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                background: "white",
                cursor: "pointer",
                marginBottom: "0.5rem",
                fontFamily: "inherit",
              }}
            >
              <option value="">All Departments</option>
              {timeTrackingStats.departmentsList.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            {/* Add Button */}
            {canEditTime && (
              <button
                onClick={handleAddTimeRecord}
                style={{
                  width: "100%",
                  padding: "0.5rem 1rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                }}
              >
                + Add Time Record
              </button>
            )}
          </div>
        ) : (
          /* Desktop: single compact controls bar */
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.625rem 1rem",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "0.75rem",
            }}
          >
            {/* Date Navigation */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", flexShrink: 0 }}>
              <button
                onClick={handlePreviousDay}
                disabled={navigationState.isPreviousDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: navigationState.isPreviousDisabled ? "not-allowed" : "pointer",
                  opacity: navigationState.isPreviousDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                  color: "var(--ui-text-secondary)",
                }}
              >
                &lt;
              </button>
              <button
                onClick={handleToday}
                style={{
                  padding: "0.375rem 0.75rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "0.8125rem",
                }}
              >
                Today
              </button>
              <button
                onClick={handleNextDay}
                disabled={navigationState.isNextDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: navigationState.isNextDisabled ? "not-allowed" : "pointer",
                  opacity: navigationState.isNextDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                  color: "var(--ui-text-secondary)",
                }}
              >
                &gt;
              </button>
              <span
                style={{
                  marginLeft: "0.25rem",
                  fontWeight: "500",
                  color: "var(--ui-text-secondary)",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                }}
              >
                {formatDateLabel(selectedDate)}
              </span>
            </div>

            {/* Divider */}
            <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />

            {/* Search — typing a day-type token filters to it (BL-006, C8):
                `late`, `piece rate`, `paid leave`, `unpaid leave`, `absent`,
                or `leave` for either kind. */}
            <input
              type="text"
              data-testid="tt-search"
              placeholder="Search employees or 'late', 'absent'..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: "160px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                fontFamily: "inherit",
              }}
            />

            {/* Department Filter */}
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{
                minWidth: "140px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                background: "white",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <option value="">All Departments</option>
              {timeTrackingStats.departmentsList.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            {/* Divider */}
            {canEditTime && (
              <>
                <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />

                {/* Add Button */}
                <button
                  onClick={handleAddTimeRecord}
                  style={{
                    padding: "0.5rem 1rem",
                    background: "var(--ui-primary)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontSize: "0.8125rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  + Add Time Record
                </button>
              </>
            )}
          </div>
        )}


        {/* C7 — a failed fetch must never read as "nobody worked". Sits above the
            table (not the full-page error return) so the date controls stay usable. */}
        {recordsFailed && (
          <div className="time-records-banner" role="alert">
            Couldn't load time records for {failedMonthLabel}. The times below may be
            incomplete — change the date or refresh to try again.
          </div>
        )}

        {/* Employee Time Records Table or Empty State */}
        {displayedEmployees.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Blue Header Bar */}
            <div
              style={{
                background: "var(--ui-gradient-primary)",
                padding: "14px 16px",
              }}
            >
              <span
                style={{
                  color: "white",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                }}
              >
                Daily Time Records
              </span>
            </div>
            {/* Empty State Content */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "4rem 2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  background: "var(--ui-primary-tint)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                  border: "3px solid var(--ui-primary)",
                }}
              >
                <Icon
                  name={emptyReason === 'no-active' ? "calendar" : "search"}
                  style={{ width: "48px", height: "48px", stroke: "var(--ui-primary)" }}
                />
              </div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "var(--ui-primary)",
                  marginBottom: "0.5rem",
                }}
              >
                {emptyReason === 'no-active'
                  ? "No Employees Active"
                  : emptyReason === 'no-late'
                    ? "No Late Arrivals"
                    : "No Matching Employees"}
              </h3>
              <p
                style={{
                  color: "var(--ui-text-muted)",
                  marginBottom: "0",
                  lineHeight: "1.6",
                  maxWidth: "400px",
                  fontSize: "0.9375rem",
                }}
              >
                {emptyReason === 'no-active' ? (
                  <>
                    No employees were active on{" "}
                    <span style={{ fontWeight: "600", color: "var(--ui-text-secondary)" }}>
                      {formatDateLabel(selectedDate)}
                    </span>
                    . Try selecting a different date or check your employee records.
                  </>
                ) : emptyReason === 'no-late' ? (
                  <>
                    None of the {filteredEmployees.length} employees active on{" "}
                    <span style={{ fontWeight: "600", color: "var(--ui-text-secondary)" }}>
                      {formatDateLabel(selectedDate)}
                    </span>
                    {" "}have a late clock-in recorded.
                  </>
                ) : (
                  <>
                    None of the {filteredEmployees.length} employees active on{" "}
                    <span style={{ fontWeight: "600", color: "var(--ui-text-secondary)" }}>
                      {formatDateLabel(selectedDate)}
                    </span>
                    {" "}match your current search or department filter.
                  </>
                )}
              </p>
            </div>
          </div>
        ) : (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              {/* minWidth 700 -> 820 for the sixth column (FLAGS). The widest pill
                  is `Unpaid Leave` and it is nowrap, so without the extra room the
                  five existing columns would be crushed to make space for it rather
                  than the wrapper scrolling. */}
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "820px" }}>
                <thead>
                  <tr style={{ background: "var(--ui-gradient-primary)" }}>
                    <th
                      onClick={() => handleSort("name")}
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Employee<SortIcon field="name" />
                    </th>
                    <th
                      onClick={() => handleSort("id")}
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ID<SortIcon field="id" />
                    </th>
                    <th
                      onClick={() => handleSort("department")}
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Department<SortIcon field="department" />
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Time In
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Time Out
                    </th>
                    {/* FLAGS sits after Time Out to match the monthly table's column
                        order exactly (PRD_TimeRecordDayFlags C1/C10) — the two pages
                        show the same records, so reading the day type in a different
                        place on each was the inconsistency.
                        It also OWNS the flag sort, which previously hung off the Time In
                        header: sorting by lateness from a header labelled Time In, with
                        the value sorted on rendered two columns away, was exactly the
                        mislabelled affordance C4 exists to prevent. Same field name
                        ("late") and same predicate as the monthly table. */}
                    <th
                      onClick={recordsFailed ? undefined : () => handleSort("late")}
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "white",
                        cursor: recordsFailed ? "default" : "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {/* Inert while the month's fetch is failed: every timeEntries is
                          empty, so both sort directions collapse to the surname tiebreak
                          and the only feedback would be an arrow flipping over a list
                          that never moves. Same reasoning as BL-083's filter suspension —
                          don't offer an affordance that cannot act. */}
                      Flags{!recordsFailed && <SortIcon field="late" />}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedEmployees.map((employee) => {
                    const lastName = employee.personalInfo?.lastName || "";
                    const firstName = employee.personalInfo?.firstName || "";
                    const fullName = lastName && firstName
                      ? `${lastName}, ${firstName}`
                      : lastName || firstName || "Unnamed Employee";
                    const department = employee.employment?.department || "No Department";
                    const employeeId = employee.employeeId || "No ID";

                    const handleEmployeeClick = () => {
                      navigate(`/company/${slug}/time-tracking/employee/${employee.employeeId}`);
                    };

                    if (employee.timeEntries.length === 0) {
                      return (
                        <tr
                          key={employee.id}
                          style={{
                            borderBottom: "1px solid var(--ui-surface-recessed)",
                            transition: "background-color 0.2s",
                            cursor: "pointer",
                          }}
                          onClick={handleEmployeeClick}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <div
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  background: "var(--ui-primary-tint)",
                                  color: "var(--ui-primary)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  flexShrink: 0,
                                }}
                              >
                                {employee.personalInfo?.firstName?.charAt(0) || ""}
                                {employee.personalInfo?.lastName?.charAt(0) || ""}
                              </div>
                              <span style={{ fontWeight: "500", color: "var(--ui-text)" }}>
                                {fullName}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                            {employeeId}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: department === "No Department" ? "var(--ui-text-faint)" : "var(--ui-text)" }}>
                            {department === "No Department" ? "Not set" : department}
                          </td>
                          {/* BL-083: '—' means "no record"; a month that didn't load is
                              unknown, not empty — render '?' so a failure can't read as
                              "nobody worked" (banner above explains). */}
                          <td style={{ padding: "12px 16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>{recordsFailed ? "?" : "—"}</td>
                          <td style={{ padding: "12px 16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>{recordsFailed ? "?" : "—"}</td>
                          <td style={{ padding: "12px 16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>{recordsFailed ? "?" : "—"}</td>
                        </tr>
                      );
                    }

                    return employee.timeEntries.map((entry, index) => (
                      <tr
                        key={`${employee.id}-${index}`}
                        style={{
                          borderBottom:
                            index === employee.timeEntries.length - 1 ? "1px solid var(--ui-surface-recessed)" : "none",
                          transition: "background-color 0.2s",
                          cursor: "pointer",
                        }}
                        onClick={handleEmployeeClick}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {index === 0 ? (
                          <>
                            <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }} rowSpan={employee.timeEntries.length}>
                              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <div
                                  style={{
                                    width: "36px",
                                    height: "36px",
                                    background: "var(--ui-primary-tint)",
                                    color: "var(--ui-primary)",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {employee.personalInfo?.firstName?.charAt(0) || ""}
                                  {employee.personalInfo?.lastName?.charAt(0) || ""}
                                </div>
                                <span style={{ fontWeight: "500", color: "var(--ui-text)" }}>
                                  {fullName}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: "12px 16px", fontVariantNumeric: "tabular-nums", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }} rowSpan={employee.timeEntries.length}>
                              {employeeId}
                            </td>
                            <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: "500", color: department === "No Department" ? "var(--ui-text-faint)" : "var(--ui-text)" }} rowSpan={employee.timeEntries.length}>
                              {department === "No Department" ? "Not set" : department}
                            </td>
                          </>
                        ) : null}
                        <td
                          style={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: !entry.timeIn ? "var(--ui-text-faint)" : "var(--ui-text)",
                          }}
                        >
                          {formatFirestoreTimestamp(entry.timeIn) || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            color: !entry.timeOut ? "var(--ui-text-faint)" : "var(--ui-text)",
                          }}
                        >
                          {entry.timeOut
                            ? formatFirestoreTimestamp(entry.timeOut)
                            : "—"}
                          {entry.isOvernight && (
                            <span
                              title={`Clocked out ${entry.timeOutDate?.toLocaleDateString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric' })}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.625rem",
                                fontWeight: "700",
                                color: "var(--ui-warning-text)",
                                background: "var(--ui-accent-tint)",
                                borderRadius: "4px",
                                padding: "1px 5px",
                                marginLeft: "4px",
                                verticalAlign: "middle",
                                lineHeight: "1.3",
                              }}
                            >+1</span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "0.875rem" }}>
                          {dayFlagOf(entry) ? (
                            <DayFlagBadge flag={dayFlagOf(entry)} />
                          ) : (
                            <span style={{ color: "var(--ui-text-faint)" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
                borderTop: "1px solid var(--ui-border)",
              }}
            >
              <div style={{ display: "flex", gap: "32px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Total Employees
                  </span>
                  <span data-testid="tt-footer-total" style={{ fontSize: "20px", fontWeight: "600", color: "var(--ui-primary)", fontVariantNumeric: "tabular-nums" }}>
                    {displayedTotal}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Clocked In
                  </span>
                  <span data-testid="tt-footer-clocked-in" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)", fontVariantNumeric: "tabular-nums" }}>
                    {recordsFailed ? "—" : displayedClockedIn}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Not Clocked In
                  </span>
                  <span data-testid="tt-footer-not-clocked-in" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)", fontVariantNumeric: "tabular-nums" }}>
                    {recordsFailed ? "—" : displayedEmployees.length - displayedClockedIn}
                  </span>
                </div>
              </div>
              <span style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)" }}>
                Click on a row to view details
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Add Time Record Modal */}
      <AddTimeRecordModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        selectedDate={selectedDate}
        companyId={companyDocId}
        onSave={handleSaveSuccess}
        source="time-tracking"
        payrollRates={payrollRates}
        payrollRatesError={payrollRatesError}
        attendancePolicy={attendancePolicy}
        userDocId={user?.userData?.userDocId || user?.uid}
      />
    </div>
  );
};